import { useCallback } from "react"
import { motion, AnimatePresence } from "framer-motion"
import { Button } from "@shared/components/ui/button"
import { XCircle } from "lucide-react"
import { cn } from "@shared/lib/utils"
import type { BulkAction } from "@shared/types/data-table"

interface BulkActionsProps {
  actions: BulkAction[]
  selectedItems: string[]
  onBulkAction: (actionKey: string, selectedItems: string[]) => void
  onClearSelection?: () => void
  className?: string
}

const ANIMATION_DURATION = {
  CONTAINER: 0.15,
  BUTTON: 0.1,
  BUTTON_DELAY: 0.03,
} as const

const ANIMATION_CONFIG = {
  container: {
    initial: { opacity: 0, y: -5 },
    animate: { opacity: 1, y: 0 },
    exit: { opacity: 0, y: -5 },
    transition: { duration: ANIMATION_DURATION.CONTAINER, ease: "easeOut" }
  },
  button: {
    initial: { opacity: 0, x: -3 },
    animate: { opacity: 1, x: 0 },
    transition: { duration: ANIMATION_DURATION.BUTTON }
  }
} as const

export const BulkActions = ({
  actions,
  selectedItems,
  onBulkAction,
  onClearSelection,
  className,
}: BulkActionsProps) => {
  const selectedCount = selectedItems.length

  const handleBulkAction = useCallback(
    (actionKey: string) => onBulkAction(actionKey, selectedItems),
    [onBulkAction, selectedItems]
  )

  if (!actions?.length || selectedCount === 0) return null

  const displayText = `${selectedCount} selected items`

  return (
    <AnimatePresence mode="wait">
      <motion.div
        {...ANIMATION_CONFIG.container}
        className={cn(
          "bg-muted border border-border rounded-full shadow-sm",
          "flex items-center gap-2 h-8 px-3 select-none",
          className
        )}
      >
        {onClearSelection && (
          <Button
            variant="ghost"
            size="sm"
            onClick={onClearSelection}
            className="h-5 w-5 p-0 rounded-full text-foreground hover:text-foreground hover:bg-accent"
            aria-label="Clear selection"
          >
            <XCircle className="h-3.5 w-3.5" />
          </Button>
        )}

        <span className="text-sm text-foreground">
          {displayText}:
        </span>

        <div className="flex items-center gap-2">
          {actions.map((action, index) => (
            <motion.div
              key={action.key}
              {...ANIMATION_CONFIG.button}
              transition={{ ...ANIMATION_CONFIG.button.transition, delay: index * ANIMATION_DURATION.BUTTON_DELAY }}
            >
              <Button
                variant="ghost"
                size="sm"
                onClick={() => handleBulkAction(action.key)}
                className={cn(
                  "h-6 w-6 p-0 rounded-full",
                  action.variant === "destructive"
                    ? "text-foreground hover:text-destructive hover:bg-accent"
                    : "text-foreground hover:text-foreground hover:bg-accent"
                )}
                title={action.label}
              >
                {action.icon || action.label.charAt(0)}
              </Button>
            </motion.div>
          ))}
        </div>
      </motion.div>
    </AnimatePresence>
  )
} 
